
import React from 'react';
import { Client } from "../../types";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Zap, CheckCircle, Loader2, Eye, Info, AlertTriangle } from 'lucide-react';
import { BackendProgressStage } from '../../enrichment-dialog/enrichmentTypes';
import { formatEstimatedCompletionTime } from '@/utils/formatters';
import { EnrichmentProgressBar } from '../../enrichment-dialog/EnrichmentProgressBar'; // Import the new component

interface EnrichmentStatusBannerProps {
  client: Client;
  lastEnrichmentProgressStage?: BackendProgressStage | string | undefined;
  estimatedCompletionTime?: string | null;
  onViewResults?: () => void;
  onEnrich: () => void;
  isEnriching: boolean;
  isPolling?: boolean;
}

export const EnrichmentStatusBanner: React.FC<EnrichmentStatusBannerProps> = ({
  client,
  lastEnrichmentProgressStage,
  estimatedCompletionTime,
  onViewResults,
  onEnrich,
  isEnriching,
  isPolling,
}) => {
  const currentEffectiveStage = lastEnrichmentProgressStage || client.enrichment_status || 'none';

  const isTerminalError = currentEffectiveStage.startsWith('error_') || currentEffectiveStage === 'error';
  const isCancelled = currentEffectiveStage === 'cancelled';
  const isCompletedSuccessfully = currentEffectiveStage === 'completed' || client.enrichment_status === 'available';
  
  const isCurrentlyProcessing = isEnriching || isPolling || (
    !isTerminalError &&
    !isCancelled &&
    !isCompletedSuccessfully &&
    client.enrichment_status !== 'none' && // 'none' means no active process
    (client.enrichment_status === 'pending' || client.enrichment_status === 'processing' || client.enrichment_status === 'pending_poll' ||
     (lastEnrichmentProgressStage && 
      !['completed', 'applied', 'error', 'error_initiating', 'error_polling', 'error_processing', 'error_unknown', 'cancelled'].includes(lastEnrichmentProgressStage)))
  );

  const formattedEstTime = formatEstimatedCompletionTime(estimatedCompletionTime);

  let alertVariant: "default" | "destructive" | "success" = "default";
  let icon = <Zap className="h-4 w-4" />;
  let title = "Company Enrichment";
  let description = "Keep your client data up-to-date with our enrichment service.";
  let actionButton = (
    <Button onClick={onEnrich} disabled={isCurrentlyProcessing || isEnriching}>
      {(isCurrentlyProcessing || isEnriching) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
      {(isCurrentlyProcessing || isEnriching) ? 'Enriching...' : 'Enrich Company Data'}
    </Button>
  );

  if (isCompletedSuccessfully) {
    alertVariant = "success";
    icon = <CheckCircle className="h-4 w-4" />;
    title = "Enrichment Data Available";
    description = `Enriched data for ${client.name} is ready. Review and apply updates.`;
    if (onViewResults) {
      actionButton = (
        <Button onClick={onViewResults} variant="outline">
          <Eye className="mr-2 h-4 w-4" />
          View Results
        </Button>
      );
    }
  } else if (isCurrentlyProcessing) {
    icon = <Loader2 className="h-4 w-4 animate-spin" />;
    title = "Enrichment In Progress";
    description = `We are currently enriching data for ${client.name}.`;
    if (formattedEstTime) {
      description += ` ${formattedEstTime}.`;
    }
  } else if (isTerminalError) {
    alertVariant = "destructive";
    icon = <AlertTriangle className="h-4 w-4" />;
    title = "Enrichment Error";
    description = `There was an issue during the last enrichment attempt for ${client.name}. You can try again.`;
  } else if (isCancelled) {
    alertVariant = "default"; // Or "warning" if you have one
    icon = <Info className="h-4 w-4" />;
    title = "Enrichment Cancelled";
    description = `The enrichment process for ${client.name} was cancelled.`;
  } else if (client.enrichment_status === 'pending_poll') {
     icon = <Info className="h-4 w-4" />; // Or Hourglass
     title = "Enrichment Pending";
     description = `Enrichment for ${client.name} is pending and will be polled for results.`;
     if (formattedEstTime) {
      description += ` ${formattedEstTime}.`;
    }
  }


  return (
    <Alert variant={alertVariant} className="mb-6">
      <div className="flex items-center">
        <span className="mr-2">{icon}</span>
        <AlertTitle>{title}</AlertTitle>
      </div>
      <AlertDescription className="pl-6 mt-1">
        {description}
      </AlertDescription>
      
      {(isCurrentlyProcessing || isCompletedSuccessfully || isTerminalError || isCancelled || client.enrichment_status === 'pending_poll') && (
         <EnrichmentProgressBar currentStage={currentEffectiveStage} className="mt-3 pl-6 pr-6" />
      )}

      <div className="mt-4 pl-6">
        {actionButton}
      </div>
    </Alert>
  );
};

